import { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import * as XLSX from "xlsx";
import PackageModal from "./PackageModal";
import PackagePaymentModal from "./PackagePaymentModal";
import PackageStatusModal from "./TreatmentStatusModal";
import TreatmentModalComponent from "./TreatmentModalComponent";
import TreatmentAmountModal from "./TreatmentAmountModal";
import CloseTreatmentModal from "./CloseTreatmentModal";
import ClosePackageTreatmentModal from "./ClosePackageTreatmentModal";
import TreatmentHistory from "./TreatmentHistory";
import PackageHistoryAndPayments from "./PatientPage/PackageHistoryAndPayments";

export default function PatientPage({ searchVPJC }) {
  const [searchText, setSearchText] = useState("");
  const [patient, setPatient] = useState(null);
  const [visits, setVisits] = useState([]);
  const [activePackage, setActivePackage] = useState(null);
  const [packageHistory, setPackageHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editedVisits, setEditedVisits] = useState({});
  const [originalTreatments, setOriginalTreatments] = useState({});
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [packagePayments, setPackagePayments] = useState([]);
  const [showClosePackageModal, setShowClosePackageModal] = useState(false);
  const [activeTreatment, setActiveTreatment] = useState(null);
  const [treatmentEntries, setTreatmentEntries] = useState([]);
  const [showTreatmentModal, setShowTreatmentModal] = useState(false);
  const [showCloseTreatmentModal, setShowCloseTreatmentModal] = useState(false);
  const [closedTreatment, setClosedTreatment] = useState(null);
  const [packageTRC, setPackageTRC] = useState(false);
  const [showPackageTRCModal, setShowPackageTRCModal] = useState(false);
  const [packageUpdating, setPackageUpdating] = useState(false);

  useEffect(() => {
    // 🔥 Clear everything when page loads
    setPatient(null);
    setVisits([]);
    setActivePackage(null);
    setPackageHistory([]);
    setSearchText("");
    setTreatmentEntries([]);
    setActiveTreatment([]);
    setClosedTreatment([]);
  }, []);

  /* ================= FETCH PATIENT ================= */
  const fetchPatient = async (value) => {
    if (!value) return;

    setLoading(true);

    // ✅ CLEAR OLD DATA IMMEDIATELY
    setPatient(null);
    setVisits([]);
    setActivePackage(null);
    setPackageHistory([]);
    setPackagePayments([]);
    try {
      let q;
      if (/^\d{10}$/.test(value)) {
        q = query(collection(db, "patients"), where("mobile", "==", value));
      } else {
        q = query(collection(db, "patients"), where("vpjc_id", "==", value));
      }

      const snap = await getDocs(q);
      if (!snap.empty) {
        const data = { id: snap.docs[0].id, ...snap.docs[0].data() };

        // 🟢 Fallback for old patients (VERY IMPORTANT)
        if (!data.package_status) {
          data.package_status = "ACTIVE";
        }

        setPatient(data);
        setSearchText(value);
        setTreatmentEntries([]);
        setActiveTreatment([]);
        setClosedTreatment([]);

        // 1️⃣ Fetch visits first, then pass them to packages
        const visitsList = await fetchVisits(data.id);
        await fetchTreatment(data.id);
        fetchActivePackage(data);
        fetchPackageHistory(data.vpjc_id, visitsList, data);
        await fetchPackagePayments(data.vpjc_id);
      } else {
        setPatient(null);
        setVisits([]);
        setActivePackage(null);
        setPackageHistory([]);
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const updateVisitTreatment = async (visitId, treatmentValue) => {
    try {
      const ref = doc(db, "visits", visitId);

      await updateDoc(ref, {
        treatment: treatmentValue,
        updated_at: serverTimestamp(),
      });

      // Remove edited flag
      setEditedVisits((prev) => {
        const updated = { ...prev };
        delete updated[visitId];
        return updated;
      });
    } catch (error) {
      console.error("Error updating treatment:", error);
    }
  };

  /* ================= FETCH VISITS ================= */
  const fetchVisits = async (patientId) => {
    const q = query(
      collection(db, "visits"),
      where("patient_id", "==", patientId),
    );
    const snap = await getDocs(q);
    const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    list.sort((a, b) => (b.token_number || 0) - (a.token_number || 0));
    setVisits(list);
    return list; // ✅ important to return visits for package calculations
  };

  /* ================= FETCH ACTIVE PACKAGE ================= */
  const fetchActivePackage = async (patientData) => {
    try {
      const q = query(
        collection(db, "packages"),
        where("patient_id", "==", patientData.id),
        where("status", "==", "ACTIVE"),
      );

      const snap = await getDocs(q);

      if (snap.empty) {
        setActivePackage(null);
        return;
      }

      const pkgList = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));

      pkgList.sort(
        (a, b) => (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0),
      );

      const pkg = pkgList[0];

      const totalDays = pkg.total_days || pkg.duration_weeks * 7;
      const usedDays = patientData?.package_used_days || 0;

      const remainingDays = Math.max(totalDays - usedDays, 0);

      // 🔥 if package finished
      if (remainingDays === 0) {
        setPackageUpdating(true);
        // 🔥 Mark package as completed in Firestore
        await updateDoc(doc(db, "packages", pkg.id), {
          status: "COMPLETED",
          completed_at: new Date(),
        });

        setPackageUpdating(false);
        setActivePackage(null);
        return;
      }

      setActivePackage({
        id: pkg.id,
        status: pkg.status, // ✅ ADD THIS
        duration_weeks: pkg.duration_weeks,
        total_days: totalDays,
        used_days: usedDays,
        remaining_days: remainingDays,
        total_amount: pkg.total_amount,
        paid_amount: pkg.paid_amount,
        due_amount: pkg.due_amount,
        consultation_payment_status: pkg.consultation_payment_status,
      });
    } catch (err) {
      console.error("Error fetching active package:", err);
      setActivePackage(null);
    }
  };

  /* ================= FETCH PACKAGE HISTORY ================= */
  const fetchPackageHistory = async (vpjcId, visitsList = [], patientData) => {
    const q = query(collection(db, "packages"), where("vpjc_id", "==", vpjcId));
    const snap = await getDocs(q);

    let list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

    // Sort newest first
    list.sort(
      (a, b) => (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0),
    );

    const packagesWithUsage = list.map((pkg) => {
      const totalDays = pkg.duration_weeks * 7;

      // Normalize start date
      let start = pkg.start_date?.toDate
        ? pkg.start_date.toDate()
        : new Date(pkg.start_date);

      start.setHours(0, 0, 0, 0);

      // 🔥 COUNT ONLY REAL PACKAGE SESSIONS (NOT ALL VISITS)
      let usedDays = patientData?.package_used_days || 0;
      let remainingDays = Math.max(totalDays - usedDays, 0);

      // If package completed → remaining should be 0
      if (pkg.status === "COMPLETED") {
        remainingDays = 0;
      }

      return {
        ...pkg,
        total_days: totalDays,
        used_days: usedDays,
        remaining_days: remainingDays,
      };
    });

    setPackageHistory(packagesWithUsage);
    const latestPackage = packagesWithUsage[0];
    await fetchPackageTreatmentClosure(latestPackage?.id);
  };

  /* ================= FETCH PACKAGE PAYMENTS (RECEIPTS) ================= */
  const fetchPackagePayments = async (vpjcId) => {
    try {
      const q = query(
        collection(db, "package_payments"),
        where("vpjc_id", "==", vpjcId),
      );

      const snap = await getDocs(q);

      const list = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));

      // Sort latest payment first
      list.sort(
        (a, b) => (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0),
      );

      setPackagePayments(list);
    } catch (err) {
      console.error("Error fetching package payments:", err);
    }
  };

  const fetchPackageTreatmentClosure = async (latestPackageId) => {
    if (!latestPackageId) {
      setPackageTRC(null);
      return;
    }

    const q = query(
      collection(db, "package_treatment_closures"),
      where("package_id", "==", latestPackageId),
    );

    const snap = await getDocs(q);

    if (!snap.empty) {
      setPackageTRC(snap.docs[0].data());
    } else {
      setPackageTRC(null);
    }
  };
  /* ================= AUTO LOAD FROM TOKEN PAGE ================= */
  useEffect(() => {
    if (searchVPJC) {
      setSearchText(searchVPJC);
      fetchPatient(searchVPJC);
    }
  }, [searchVPJC]);

  /* ================= CALCULATIONS ================= */
  const normalizeStatus = (v) => {
    if (v.consultation_payment_status === "PAID") return "CONSULTATION";
    if (v.consultation_payment_status === "PACKAGE") return "PACKAGE";
    if (v.consultation_payment_status === "PACKAGE_PURCHASE") return "PACKAGE";
    if (v.consultation_payment_status === "FREE_REVIEW") return "REVIEW";
    return "UNPAID";
  };

  const paidVisits = visits.filter(
    (v) => v.consultation_payment_status === "PAID",
  );
  const unpaidVisits = visits.filter(
    (v) => v.consultation_payment_status !== "PAID",
  );

  // 💰 Consultation revenue
  const totalConsultationPaid = paidVisits.reduce(
    (sum, v) => sum + Number(v.consultation_fee || 0),
    0,
  );
  // Treatment revenue
  // 🔹 Visit-based treatment payments
  const visitTreatmentPaid = visits
    ?.filter(
      (v) =>
        v.visit_category === "TREATMENT" &&
        v.treatment_payment_status === "PAID",
    )
    .reduce((sum, v) => sum + (Number(v.treatment_amount) || 0), 0);

  // 🔹 TRT (start) payments
  const startTreatmentPaid = treatmentEntries
    ?.filter((e) => !e.visit_id)
    .reduce((sum, e) => sum + (Number(e.amount) || 0), 0);

  // 🔥 FINAL
  const totalTreatmentPaid = visitTreatmentPaid + startTreatmentPaid;
  // 📦 Package revenue (NEW)
  const totalPackageAmount = packageHistory.reduce(
    (sum, p) => sum + Number(p.paid_amount || 0),
    0,
  );

  const totalDueAmount = packageHistory.reduce(
    (sum, p) => sum + Number(p.due_amount || 0),
    0,
  );

  // 🧾 GRAND TOTAL (what patient paid till date)
  const totalPaidAmount =
    totalConsultationPaid + totalPackageAmount + totalTreatmentPaid;

  const totalVisits = visits.filter(
    (v) => v.token_number !== "TRC" && v.token_number !== "PKG",
  ).length;

  // 🏥 HOW MANY PACKAGES TAKEN (YOU ASKED THIS)
  const totalPackagesTaken = packageHistory.length;

  const latestPackage = packageHistory[0];
  const hasDue = totalDueAmount > 0;
  const isPackageCompleted = ["COMPLETED", "CLOSED"].includes(
    latestPackage?.status?.toUpperCase()?.trim(),
  );

  const isTRCDone = !!packageTRC;

  /* ================= EXPORT TO EXCEL ================= */
  const exportToExcel = () => {
    if (!visits.length) return;
    const data = visits.map((v) => ({
      Token:
        v.token_number === "PKG"
          ? "PKG"
          : `T-${String(v.token_number).padStart(3, "0")}`,
      Date: v.visit_date,
      Payment: normalizeStatus(v),
      Amount:
        normalizeStatus(v) === "PAID"
          ? v.consultation_fee || 0
          : normalizeStatus(v) === "PACKAGE"
            ? "Package"
            : "—",
    }));
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Visits");
    XLSX.writeFile(workbook, `${patient.name}_visits.xlsx`);
  };
  console.log(activePackage, "package");
  console.log("STATUS:", packageHistory[0]?.status);
  console.log("latestPackage:", packageHistory[0]);

  const fetchTreatmentEntries = async (treatments) => {
    let allEntries = [];

    for (const t of treatments) {
      const q = query(
        collection(db, "treatment_entries"),
        where("treatment_id", "==", t.id),
      );

      const snap = await getDocs(q);
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      allEntries.push(...list);
    }

    setTreatmentEntries(allEntries);
  };

  const fetchTreatment = async (patientId) => {
    const q = query(
      collection(db, "treatments"),
      where("patient_id", "==", patientId),
    );

    const snap = await getDocs(q);

    if (!snap.empty) {
      const treatments = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));

      const activeTreatments = treatments
        .filter((t) => t.status === "ACTIVE")
        .sort(
          (a, b) => (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0),
        );

      const closedTreatments = treatments
        .filter((t) => t.status === "COMPLETED")
        .sort(
          (a, b) =>
            (b.completed_at?.seconds || 0) - (a.completed_at?.seconds || 0),
        );

      setActiveTreatment(activeTreatments);
      setClosedTreatment(closedTreatments);

      // ✅ FIXED: fetch entries for ALL treatments
      await fetchTreatmentEntries([...activeTreatments, ...closedTreatments]);
    } else {
      setTreatmentEntries([]);
      setActiveTreatment([]);
      setClosedTreatment([]);
    }
  };
  const startEntry = treatmentEntries.find((e) => !e.visit_id);
  const allTreatments = [
    ...(activeTreatment || []),
    ...(closedTreatment || []),
  ];

  const treatmentRows = allTreatments.map((t) => {
    const startEntry = treatmentEntries.find(
      (e) => e.treatment_id === t.id && !e.visit_id,
    );

    return {
      id: "TRT-" + t.id,
      token_number: "TRT",
      created_at: t.created_at,
      visit_date: t.start_date,
      treatment: t.treatment_name || "Treatment",
      consultation_payment_status: "TREATMENT",
      treatment_display: true,
      treatment_amount: startEntry?.amount || null,
    };
  });
  console.log(
    startEntry,
    "startEntry",
    treatmentEntries.find((e) => e.amount),
  );

  const treatmentClosedRow = (closedTreatment || []).map((t) => ({
    id: "TRC-" + t.id,
    token_number: "TRC",
    created_at: t.completed_at,
    visit_date: t.completed_at,
    treatment: "Treatment Closed",
    consultation_payment_status: "TREATMENT",
    treatment_display: true,
  }));

  const packageTRCRow =
    packageTRC && patient
      ? [
          {
            id: "PKGTRC-" + patient.id,
            token_number: "TRC",
            created_at: packageTRC.completed_at || new Date(),
            visit_date: packageTRC.completed_at,
            treatment: "Treatment Completed",
            consultation_payment_status: "TREATMENT",
            treatment_display: true,
          },
        ]
      : [];
  // console.log("packageTRC:", packageTRC);
  // ================= FINAL TREATMENT LOGIC =================

  // 🟢 START ENTRY (TRT)
  const startTreatmentEntry = treatmentEntries.find((e) => !e.visit_id);
  console.log(
    startTreatmentEntry?.amount,
    "startTreatmentEntry?.amount",
    startTreatmentEntry,
  );

  // 🔵 VISIT MAP
  const treatmentMap = {};
  treatmentEntries.forEach((e) => {
    if (e.visit_id) {
      treatmentMap[e.visit_id] = e.amount;
    }
  });

  // 👉 Merge with visits
  const allRows = [
    ...treatmentRows,
    ...treatmentClosedRow,
    ...packageTRCRow,
    ...visits,
  ].sort((a, b) => {
    const getTime = (item) => {
      if (item.created_at?.seconds) {
        return item.created_at.seconds;
      }
      return new Date(item.created_at || item.visit_date).getTime() / 1000;
    };

    return getTime(b) - getTime(a);
  });

  console.log("latestPackage1", latestPackage);
  console.log("packageTRC1", packageTRC);
  /* ================= UI ================= */
  return (
    <div className="bg-gray-100 min-h-screen">
      <div className="p-4 md:p-6 space-y-6">
        {/* SEARCH BAR */}
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            value={searchText}
            onChange={(e) => {
              const value = e.target.value;
              setSearchText(value);

              // ✅ If search box is cleared, remove patient data
              if (value.trim() === "") {
                setPatient(null);
                setVisits([]);
                setActivePackage(null);
                setPackageHistory([]);
                setPackagePayments([]);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                fetchPatient(searchText.trim());
              }
            }}
            placeholder="Search by VPJC ID or Mobile"
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-[#204080]/20 focus:border-[#204080]"
          />
          <button
            onClick={() => {
              const value = searchText.trim();
              if (!value) return;
              fetchPatient(value);
            }}
            className="w-full sm:w-auto px-4 py-2 rounded-lg bg-[#204080] hover:bg-[#1a356b] text-white font-semibold transition"
          >
            Search
          </button>
        </div>

        {loading && <p className="text-center text-gray-500">Loading…</p>}

        {patient && (
          <>
            {/* PATIENT HEADER */}
            <div className="bg-[#204080] text-white rounded-xl shadow-sm px-6 py-5">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
                {/* Left: Patient Info */}
                <div>
                  <h2 className="text-2xl font-semibold tracking-tight">
                    {patient.name}
                  </h2>

                  <p className="mt-2 text-sm text-white/80">
                    <span className="font-medium text-white">
                      {patient.vpjc_id}
                    </span>
                    <span className="mx-2 text-white">|</span>
                    <span className="font-medium text-white">
                      {patient.mobile}
                    </span>
                  </p>
                  {patient.referred_by && (
                    <p className="mt-1 text-sm text-white">
                      Referred By:{" "}
                      <span className="font-medium text-white">
                        {patient.referred_by.replace(/\b\w/g, (char) =>
                          char.toUpperCase(),
                        )}
                      </span>
                    </p>
                  )}
                </div>

                {/* Right: Status + Actions */}
                <div className="flex items-center gap-3 flex-wrap">
                  {activeTreatment?.length > 0 && (
                    <span className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white/15 border border-white/20">
                      Active Treatment
                    </span>
                  )}
                  {/* Package Status */}
                  {
                    activePackage ? (
                      <span className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white/15 border border-white/20">
                        Active Package · {activePackage.duration_weeks}{" "}
                        {activePackage.duration_weeks === 1 ? "Week" : "Weeks"}
                      </span>
                    ) : (
                      ""
                    )
                    // : (
                    // <span className="px-3 py-1.5 rounded-lg text-xs font-medium bg-white/15 border border-white/20">
                    //   No Active Package
                    // </span>
                    // )
                  }

                  {/* Add Package Button */}
                  <button
                    onClick={() => {
                      if (activePackage) return; // 🚫 stop opening modal
                      setShowPackageModal(true);
                    }}
                    disabled={!!activePackage}
                    className={`px-4 py-2 text-xs font-semibold rounded-lg text-white transition shadow-sm ${
                      activePackage?.remaining_days > 0
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-purple-500 hover:bg-purple-600"
                    }`}
                  >
                    + Package
                  </button>

                  {/* Add Payment Button */}
                  {hasDue && (
                    <button
                      onClick={() => setShowPaymentModal(true)}
                      className="px-4 py-2 text-xs font-semibold rounded-lg bg-green-500 hover:bg-green-600 text-white transition shadow-sm"
                    >
                      + Payment
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (activeTreatment?.length > 0) return; // 🚫 stop
                      setShowTreatmentModal(true);
                    }}
                    disabled={activeTreatment?.length > 0}
                    className={`px-4 py-2 text-xs font-semibold rounded-lg text-white transition shadow-sm ${
                      activeTreatment?.length > 0
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-blue-500 hover:bg-blue-600"
                    }`}
                  >
                    + Treatment
                  </button>
                  {activeTreatment?.length > 0 && (
                    <button
                      onClick={() => setShowCloseTreatmentModal(true)}
                      className="px-4 py-2 text-xs font-semibold rounded-lg bg-red-500 text-white"
                    >
                      Close Treatment
                    </button>
                  )}

                  {/* Close Package */}
                  {activePackage && (
                    <button
                      onClick={() => setShowClosePackageModal(true)}
                      className="px-4 py-2 text-xs font-semibold rounded-lg bg-red-500 hover:bg-red-600 text-white transition shadow-sm"
                    >
                      Close Package
                    </button>
                  )}

                  {latestPackage && isPackageCompleted && !isTRCDone && (
                    <button
                      onClick={() => setShowPackageTRCModal(true)}
                      className="px-4 py-2 text-xs font-semibold rounded-lg bg-orange-500 text-white"
                    >
                      Close Treatment
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* SUMMARY CARDS */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4 mt-4">
              <Summary title="Total Visits" value={totalVisits} accent="blue" />

              <Summary
                title="Packages Taken"
                value={totalPackagesTaken}
                accent="purple"
              />

              <Summary
                title="Consultation Paid"
                value={`₹ ${totalConsultationPaid}`}
                accent="green"
              />

              <Summary
                title="Treatment Paid"
                value={`₹ ${totalTreatmentPaid}`}
                accent="blue"
              />

              <Summary
                title="Total Paid Till Date"
                value={`₹ ${totalPaidAmount}`}
                accent="emerald"
              />

              <Summary
                title="Package Due Balance"
                value={`₹ ${totalDueAmount}`}
                accent="red"
              />
            </div>
            {/* ACTIONS */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={exportToExcel}
                className="px-4 py-2 bg-[#204080] text-white rounded hover:bg-[#1a356b] font-semibold"
              >
                Export Visits
              </button>
            </div>

            {/* VISITS TABLE */}
            <div className="bg-white rounded-xl shadow overflow-x-auto mt-4">
              <table className="w-full text-sm border-collapse text-center">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 border-b text-[#204080]">Token</th>
                    <th className="px-3 py-2 border-b text-[#204080]">Date</th>
                    <th className="px-3 py-2 border-b text-[#204080]">
                      Treatment
                    </th>
                    <th className="px-3 py-2 border-b text-[#204080]">
                      Payment
                    </th>
                    <th className="px-3 py-2 border-b text-[#204080]">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {allRows.map((v) => {
                    const status = normalizeStatus(v);

                    return (
                      <tr
                        key={v.id}
                        className={`border-t ${
                          v.consultation_payment_status === "PACKAGE_PURCHASE"
                            ? "bg-blue-50"
                            : ""
                        }`}
                      >
                        {/* TOKEN */}
                        <td className="px-3 py-2 font-semibold">
                          {v.token_number === "PKG"
                            ? "PKG"
                            : v.token_number === "TRT"
                              ? "TRT"
                              : v.token_number === "TRC"
                                ? "TRC"
                                : `T-${String(v.token_number).padStart(3, "0")}`}
                        </td>

                        {/* DATE */}
                        <td className="px-3 py-2">
                          {v.visit_date
                            ? v.visit_date.toDate
                              ? v.visit_date
                                  .toDate()
                                  .toLocaleDateString("en-IN")
                              : new Date(v.visit_date).toLocaleDateString(
                                  "en-IN",
                                )
                            : v.created_at?.toDate
                              ? v.created_at
                                  .toDate()
                                  .toLocaleDateString("en-IN")
                              : "-"}
                        </td>

                        {/* ✅ TREATMENT COLUMN */}
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2 justify-center">
                            <input
                              type="text"
                              value={
                                v.treatment ||
                                (v.consultation_payment_status ===
                                "PACKAGE_PURCHASE"
                                  ? "Package Purchase"
                                  : v.consultation_payment_status === "PACKAGE"
                                    ? "Package Session"
                                    : v.consultation_payment_status ===
                                        "FREE_REVIEW"
                                      ? "Review"
                                      : "Consultation")
                              }
                              onChange={(e) => {
                                const value = e.target.value;

                                // Save original value first time editing
                                if (!editedVisits[v.id]) {
                                  setOriginalTreatments((prev) => ({
                                    ...prev,
                                    [v.id]: v.treatment || "",
                                  }));
                                }

                                // Update local state
                                setVisits((prev) =>
                                  prev.map((item) =>
                                    item.id === v.id
                                      ? { ...item, treatment: value }
                                      : item,
                                  ),
                                );

                                // Mark as edited
                                setEditedVisits((prev) => ({
                                  ...prev,
                                  [v.id]: true,
                                }));
                              }}
                              placeholder="Enter Treatment"
                              className="border rounded px-2 py-1 text-xs w-28 text-center"
                            />

                            {/* Show buttons only when editing */}
                            {editedVisits[v.id] && (
                              <>
                                {/* SAVE BUTTON */}
                                <button
                                  onClick={() =>
                                    updateVisitTreatment(v.id, v.treatment)
                                  }
                                  className="bg-green-600 text-white text-xs px-2 py-1 rounded hover:bg-green-700"
                                >
                                  Save
                                </button>

                                {/* ❌ CANCEL BUTTON */}
                                <button
                                  onClick={() => {
                                    // Restore original value
                                    setVisits((prev) =>
                                      prev.map((item) =>
                                        item.id === v.id
                                          ? {
                                              ...item,
                                              treatment:
                                                originalTreatments[v.id],
                                            }
                                          : item,
                                      ),
                                    );

                                    // Remove edited flag
                                    setEditedVisits((prev) => {
                                      const updated = { ...prev };
                                      delete updated[v.id];
                                      return updated;
                                    });
                                  }}
                                  className="bg-red-500 text-white text-xs px-2 py-1 rounded hover:bg-red-600"
                                >
                                  X
                                </button>
                              </>
                            )}
                          </div>
                        </td>

                        {/* PAYMENT STATUS */}
                        <td className="px-3 py-2 font-semibold">
                          {v.consultation_payment_status === "TREATMENT"
                            ? "TREATMENT"
                            : status}
                        </td>

                        {/* AMOUNT */}
                        {/* AMOUNT */}
                        <td className="px-3 py-2">
                          {v.consultation_payment_status === "TREATMENT" && (
                            <span className="text-purple-600 font-semibold">
                              {/* TRC */}
                              {v.token_number === "TRC"
                                ? "Treatment Closed"
                                : /* TRT */
                                  v.treatment_display
                                  ? v.treatment_amount
                                    ? `₹ ${v.treatment_amount} (Treatment Started)`
                                    : "Treatment Started"
                                  : /* VISITS */
                                    v.treatment_amount
                                    ? `₹ ${v.treatment_amount} (Treatment)`
                                    : treatmentMap[v.id]
                                      ? `₹ ${treatmentMap[v.id]} (Treatment)`
                                      : "—"}
                            </span>
                          )}
                          {/* Consultation Paid */}
                          {v.consultation_payment_status === "PAID" &&
                            `₹ ${v.consultation_fee || 0}`}

                          {/* Package Purchase */}
                          {v.consultation_payment_status ===
                            "PACKAGE_PURCHASE" && (
                            <span className="text-blue-700 font-semibold">
                              ₹ {v.consultation_fee || 0} (Paid for Package)
                            </span>
                          )}

                          {/* Package Session */}
                          {v.consultation_payment_status === "PACKAGE" && (
                            <span
                              className={`font-semibold ${
                                v.package_used_snapshot ===
                                patient?.package_total_days
                                  ? "text-green-600"
                                  : "text-blue-500"
                              }`}
                            >
                              {v.package_used_snapshot ===
                              patient?.package_total_days
                                ? "Package Completed"
                                : "Package Session"}
                            </span>
                          )}

                          {/* Unpaid */}
                          {v.consultation_payment_status === "UNPAID" && "—"}
                          {v.consultation_payment_status === "FREE_REVIEW" && (
                            <span className="text-green-600 font-semibold">
                              Review
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* PACKAGE HISTORY TABLE */}
            {packageHistory.length > 0 && (
              <div className="bg-white rounded-xl shadow overflow-x-auto mt-6">
                <h3 className="text-lg font-semibold p-4 text-[#204080]">
                  Package History
                </h3>
                <table className="w-full text-sm border-collapse text-center">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 border-b text-[#204080]">
                        Created At
                      </th>
                      <th className="px-3 py-2 border-b text-[#204080]">
                        Start Date
                      </th>

                      <th className="px-3 py-2 border-b text-[#204080]">
                        Duration (Weeks)
                      </th>
                      <th className="px-3 py-2 border-b text-[#204080]">
                        Remaining Days
                      </th>
                      <th className="px-3 py-2 border-b text-[#204080]">
                        Total Days
                      </th>
                      <th className="px-3 py-2 border-b text-[#204080]">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {packageHistory.map((p) => (
                      <tr key={p.id} className="border-t">
                        {/* CREATED DATE (Date only) */}
                        <td className="px-3 py-2">
                          {p.created_at
                            ? (p.created_at.toDate
                                ? p.created_at.toDate()
                                : new Date(p.created_at)
                              ).toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "long",
                                year: "numeric",
                              })
                            : "-"}
                        </td>
                        {/* START DATE (Date only) */}
                        <td className="px-3 py-2">
                          {p.start_date
                            ? (p.start_date.toDate
                                ? p.start_date.toDate()
                                : new Date(p.start_date)
                              ).toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "long",
                                year: "numeric",
                              })
                            : "-"}
                        </td>
                        <td className="px-3 py-2">{p.duration_weeks}</td>
                        <td className="px-3 py-2">{p.remaining_days}</td>
                        <td className="px-3 py-2">{p.total_days}</td>
                        <td
                          className={`px-3 py-2 font-semibold ${
                            p.remaining_days === 0
                              ? "text-blue-600"
                              : p.status === "ACTIVE"
                                ? "text-green-600"
                                : "text-red-600"
                          }`}
                        >
                          {p.remaining_days === 0 ? "COMPLETED" : p.status}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {/* 💰 PACKAGE PAYMENT RECEIPTS */}
            {packagePayments.length > 0 && (
              <div className="bg-white rounded-xl shadow overflow-x-auto mt-6">
                <h3 className="text-lg font-semibold p-4 text-[#204080]">
                  Payment Receipts
                </h3>

                <table className="w-full text-sm border-collapse text-center">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 border-b text-[#204080]">
                        Payment Date
                      </th>
                      <th className="px-3 py-2 border-b text-[#204080]">
                        Amount Received
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {packagePayments.map((pay) => (
                      <tr key={pay.id} className="border-t">
                        <td className="px-3 py-2">
                          {pay.created_at
                            ? (pay.created_at.toDate
                                ? pay.created_at.toDate()
                                : new Date(pay.created_at)
                              ).toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "long",
                                year: "numeric",
                              })
                            : "-"}
                        </td>

                        <td className="px-3 py-2 font-semibold text-green-600">
                          ₹ {pay.amount}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <TreatmentHistory
              activeTreatment={activeTreatment}
              closedTreatment={closedTreatment}
              treatmentEntries={treatmentEntries}
              visits={visits}
            />
          </>
        )}
      </div>
      {showPackageModal && patient && (
        <PackageModal
          patient={patient}
          onClose={() => setShowPackageModal(false)}
          onSuccess={async () => {
            setShowPackageModal(false);
            await fetchPatient(patient.vpjc_id);
          }}
        />
      )}
      {showPaymentModal &&
        (() => {
          const latestPkgWithDue = packageHistory
            .filter((p) => Number(p.due_amount) > 0) // ✅ find due package
            .sort(
              (a, b) =>
                (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0),
            )[0]; // 🔥 Latest ACTIVE package only

          if (!latestPkgWithDue) return null;

          return (
            <PackagePaymentModal
              activePackageDoc={latestPkgWithDue}
              onClose={() => setShowPaymentModal(false)}
              onSuccess={async () => {
                await fetchPatient(patient.vpjc_id);
              }}
            />
          );
        })()}
      {showClosePackageModal && (
        <PackageStatusModal
          patient={patient}
          activePackage={activePackage}
          totalDueAmount={totalDueAmount}
          visits={visits}
          onClose={() => setShowClosePackageModal(false)}
          onSuccess={async () => {
            await fetchPatient(patient.vpjc_id);
          }}
        />
      )}
      {showPackageTRCModal && (
        <ClosePackageTreatmentModal
          patient={patient}
          latestPackage={packageHistory[0]}
          totalDueAmount={totalDueAmount}
          visits={visits}
          onClose={() => setShowPackageTRCModal(false)}
          onSuccess={async () => {
            await fetchPatient(patient.vpjc_id);
          }}
        />
      )}
      {showTreatmentModal && patient && (
        <TreatmentModalComponent
          patient={patient}
          onClose={() => setShowTreatmentModal(false)}
          onSuccess={async () => {
            await fetchPatient(patient.vpjc_id);
          }}
        />
      )}
      {showCloseTreatmentModal && (
        <CloseTreatmentModal
          patient={patient}
          activeTreatment={activeTreatment}
          visits={visits}
          onClose={() => setShowCloseTreatmentModal(false)}
          onSuccess={async () => {
            await fetchPatient(patient.vpjc_id);
          }}
        />
      )}
    </div>
  );
}

/* ================= SUMMARY CARD ================= */
function Summary({ title, value }) {
  return (
    <div className="bg-white p-4 rounded-xl shadow text-center hover:shadow-lg transition duration-200">
      <p className="text-xs text-gray-500">{title}</p>
      <p className="text-lg font-semibold text-[#204080]">{value}</p>
    </div>
  );
}
